/**
 * RecurrenceTemplates — built-in presets for common recurring patterns.
 *
 * Applied via RecurrencePicker "Use template" button.
 * Each template provides a pre-configured RecurrenceConfig.
 */

import type { RecurrenceConfig } from "./RecurrencePicker"

export interface RecurrenceTemplate {
  id: string
  name: string
  icon: string
  category: "health" | "productivity" | "household" | "finance" | "social"
  description: string
  config: Partial<RecurrenceConfig>
  /** Progressive overload suggestion (if applicable) */
  progressive?: {
    field: string
    start: number
    increment: number
    unit: string
    per: "instance" | "week" | "month"
    max?: number
  }
}

export const RECURRENCE_TEMPLATES: RecurrenceTemplate[] = [
  // --- Health & Fitness ---
  {
    id: "run-3x-week",
    name: "Running 3×/week",
    icon: "🏃",
    category: "health",
    description: "Flexible: 3 runs per week, any days",
    config: {
      mode: "FIXED",
      frequency: "weekly",
      interval: 1,
      weekdays: [0, 2, 4], // Mon, Wed, Fri
      flexibleCount: 3,
      flexiblePeriod: "week",
    },
    progressive: {
      field: "distance",
      start: 3,
      increment: 0.5,
      unit: "km",
      per: "week",
      max: 10,
    },
  },
  {
    id: "gym-daily",
    name: "Daily workout",
    icon: "💪",
    category: "health",
    description: "Every day, skip on rest days",
    config: {
      mode: "FIXED",
      frequency: "daily",
      interval: 1,
      pilePolicy: "NO_PILE",
    },
  },
  {
    id: "meditation",
    name: "Meditation",
    icon: "🧘",
    category: "health",
    description: "Daily mindfulness practice",
    config: {
      mode: "FIXED",
      frequency: "daily",
      interval: 1,
      pilePolicy: "NO_PILE",
    },
    progressive: {
      field: "duration",
      start: 5,
      increment: 1,
      unit: "min",
      per: "week",
      max: 30,
    },
  },
  {
    id: "drink-water",
    name: "Drink water",
    icon: "💧",
    category: "health",
    description: "8 glasses daily",
    config: {
      mode: "FIXED",
      frequency: "daily",
      interval: 1,
      flexibleCount: 8,
      flexiblePeriod: "week", // actually daily, but we use the window as weekly tracking
      pilePolicy: "NO_PILE",
    },
  },

  // --- Productivity ---
  {
    id: "weekly-review",
    name: "Weekly review",
    icon: "📋",
    category: "productivity",
    description: "Review tasks and plan next week (Sunday evening)",
    config: {
      mode: "FIXED",
      frequency: "weekly",
      interval: 1,
      weekdays: [6], // Sunday
      pilePolicy: "NO_PILE",
    },
  },
  {
    id: "daily-plan",
    name: "Plan tomorrow",
    icon: "📝",
    category: "productivity",
    description: "End-of-day planning (every workday evening)",
    config: {
      mode: "FIXED",
      frequency: "weekly",
      interval: 1,
      weekdays: [0, 1, 2, 3, 4], // Mon-Fri
      pilePolicy: "NO_PILE",
    },
  },
  {
    id: "inbox-zero",
    name: "Inbox zero",
    icon: "📥",
    category: "productivity",
    description: "Process inbox twice daily",
    config: {
      mode: "FIXED",
      frequency: "daily",
      interval: 1,
      pilePolicy: "NO_PILE",
    },
  },
  {
    id: "read-30min",
    name: "Read 30 minutes",
    icon: "📚",
    category: "productivity",
    description: "Daily reading habit",
    config: {
      mode: "FIXED",
      frequency: "daily",
      interval: 1,
      pilePolicy: "NO_PILE",
    },
  },

  // --- Household ---
  {
    id: "clean-weekly",
    name: "Weekly cleaning",
    icon: "🧹",
    category: "household",
    description: "Clean the house every Saturday",
    config: {
      mode: "FIXED",
      frequency: "weekly",
      interval: 1,
      weekdays: [5], // Saturday
    },
  },
  {
    id: "laundry",
    name: "Laundry",
    icon: "👕",
    category: "household",
    description: "Every 3 days",
    config: {
      mode: "FIXED",
      frequency: "daily",
      interval: 3,
      pilePolicy: "NO_PILE",
    },
  },
  {
    id: "groceries",
    name: "Grocery shopping",
    icon: "🛒",
    category: "household",
    description: "Weekly shopping",
    config: {
      mode: "FIXED",
      frequency: "weekly",
      interval: 1,
      weekdays: [5], // Saturday
    },
  },
  {
    id: "water-plants",
    name: "Water plants",
    icon: "🌱",
    category: "household",
    description: "Every 3 days",
    config: {
      mode: "AFTER_COMPLETION",
      afterCompletionDays: 3,
    },
  },

  // --- Finance ---
  {
    id: "budget-monthly",
    name: "Review budget",
    icon: "💰",
    category: "finance",
    description: "Monthly budget review on the 1st",
    config: {
      mode: "FIXED",
      frequency: "monthly",
      interval: 1,
      monthDay: 1,
    },
  },
  {
    id: "pay-bills",
    name: "Pay bills",
    icon: "💳",
    category: "finance",
    description: "Monthly on the 25th",
    config: {
      mode: "FIXED",
      frequency: "monthly",
      interval: 1,
      monthDay: 25,
    },
  },

  // --- Social ---
  {
    id: "call-family",
    name: "Call family",
    icon: "📞",
    category: "social",
    description: "Weekly check-in (Sunday)",
    config: {
      mode: "FIXED",
      frequency: "weekly",
      interval: 1,
      weekdays: [6], // Sunday
    },
  },
  {
    id: "date-night",
    name: "Date night",
    icon: "❤️",
    category: "social",
    description: "Weekly quality time (Friday)",
    config: {
      mode: "FIXED",
      frequency: "weekly",
      interval: 1,
      weekdays: [4], // Friday
    },
  },
]

export const TEMPLATE_CATEGORIES = [
  { id: "health", label: "Health & Fitness" },
  { id: "productivity", label: "Productivity" },
  { id: "household", label: "Household" },
  { id: "finance", label: "Finance" },
  { id: "social", label: "Social" },
] as const
